/**
 * Unit preference + conversion helpers.
 *
 * Data stays metric in Dexie (mm for needles, meters for yardage,
 * stitches per 10 cm for gauge). Display converts based on the user's
 * chosen measurement system.
 *
 * Grams stay grams regardless of preference — even US yarn labels use
 * grams globally. Yarn weight class (Fingering, DK, Worsted, Aran…) is
 * universal and needs no conversion.
 */

import { useEffect, useState } from 'react';

export type MeasurementSystem = 'metric' | 'imperial';

const STORAGE_KEY = 'knittapp-measurement-system';
const DEFAULT: MeasurementSystem = 'metric';

export function getMeasurementSystem(): MeasurementSystem {
  if (typeof window === 'undefined') return DEFAULT;
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw === 'imperial' ? 'imperial' : 'metric';
}

export function setMeasurementSystem(system: MeasurementSystem) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, system);
  window.dispatchEvent(new CustomEvent('measurement-system-changed'));
}

/** React hook that returns the current measurement system and re-renders
 *  on changes triggered by `setMeasurementSystem` in the same tab. */
export function useMeasurementSystem(): MeasurementSystem {
  const [system, setSystem] = useState<MeasurementSystem>(DEFAULT);
  useEffect(() => {
    setSystem(getMeasurementSystem());
    const handler = () => setSystem(getMeasurementSystem());
    window.addEventListener('measurement-system-changed', handler);
    window.addEventListener('storage', handler); // cross-tab
    return () => {
      window.removeEventListener('measurement-system-changed', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);
  return system;
}

/** Canonical mm ↔ US needle size table. Keyed by metric string so the
 *  conversion round-trips without floating-point surprises. */
export const NEEDLE_SIZE_TABLE: { mm: string; us: string }[] = [
  { mm: '2.0', us: 'US 0' },
  { mm: '2.25', us: 'US 1' },
  { mm: '2.5', us: 'US 1.5' },
  { mm: '2.75', us: 'US 2' },
  { mm: '3.0', us: 'US 2.5' },
  { mm: '3.25', us: 'US 3' },
  { mm: '3.5', us: 'US 4' },
  { mm: '3.75', us: 'US 5' },
  { mm: '4.0', us: 'US 6' },
  { mm: '4.5', us: 'US 7' },
  { mm: '5.0', us: 'US 8' },
  { mm: '5.5', us: 'US 9' },
  { mm: '6.0', us: 'US 10' },
  { mm: '6.5', us: 'US 10.5' },
  { mm: '7.0', us: 'US 10.75' },
  { mm: '8.0', us: 'US 11' },
  { mm: '9.0', us: 'US 13' },
  { mm: '10.0', us: 'US 15' },
  { mm: '12.0', us: 'US 17' },
  { mm: '15.0', us: 'US 19' },
  { mm: '18.0', us: 'US 35' },
];

const mmToUs = new Map(NEEDLE_SIZE_TABLE.map(n => [n.mm, n.us]));
const usToMm = new Map(NEEDLE_SIZE_TABLE.map(n => [n.us, n.mm]));

/** Normalise "4.0mm" / "4 mm" / "4mm" / "4.0" → "4.0". Returns the raw
 *  input unchanged if it doesn't match a known size — we never want to
 *  display a blank where the user typed something custom. */
function normaliseMm(raw: string): string {
  const cleaned = raw.trim().replace(/\s*mm\s*$/i, '');
  return cleaned;
}

/** Format a needle size stored as "4.0mm" / "4.0" → either "4.0mm" or
 *  "US 6" based on the current preference. Unknown sizes fall through
 *  with the raw input (wrapped with "mm" for metric) so the user's data
 *  is never lost. */
export function formatNeedleSize(rawSize: string, system: MeasurementSystem): string {
  if (!rawSize) return '';
  const mm = normaliseMm(rawSize);
  if (system === 'metric') {
    // If it came in as a US label, look up the mm for display.
    if (rawSize.trim().toUpperCase().startsWith('US')) {
      const mapped = usToMm.get(rawSize.trim());
      return mapped ? `${mapped}mm` : rawSize;
    }
    return `${mm}mm`;
  }
  // imperial
  const us = mmToUs.get(mm);
  return us ?? rawSize;
}

/** Yardage: stored in meters. Display meters or yards based on
 *  preference. `yardage` is a number; 0 / undefined → ''. */
export function formatYardage(meters: number | undefined, system: MeasurementSystem): string {
  if (!meters || !isFinite(meters) || meters <= 0) return '';
  if (system === 'metric') return `${Math.round(meters)}m`;
  const yards = Math.round(meters * 1.0936133);
  return `${yards}yd`;
}

/** Label for the per-skein length field. "Meters / skein" vs. "Yards / skein". */
export function perSkeinLengthLabel(system: MeasurementSystem): string {
  return system === 'metric' ? 'm / skein' : 'yd / skein';
}

/** Gauge: stored as stitches (or rows) per 10 cm. Display per 10 cm or
 *  per 4 inches. 10 cm ≈ 3.94 inches → the "per 4 inches" value is the
 *  stored number × (4 / 3.937…) = × 1.016. Traditional knitters commonly
 *  quote per-4" at the same number as per-10cm within 2%, so we round to
 *  an integer. */
export function convertGaugeCount(perTenCm: number, system: MeasurementSystem): number {
  if (!isFinite(perTenCm) || perTenCm <= 0) return perTenCm;
  if (system === 'metric') return perTenCm;
  return Math.round(perTenCm * 1.016);
}

/** Suffix for a gauge count ("per 10 cm" vs. "per 4 in"). */
export function gaugeUnitSuffix(system: MeasurementSystem): string {
  return system === 'metric' ? 'per 10 cm' : 'per 4"';
}
